<template>
  <div class="home">
    <Head>
      <title>BacktoWorkConnect.ie - Ireland's leading career hub for Returners and career changers</title>
      <link rel="canonical" href="https://backtoworkconnect.ie" />
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
      <link head-key="font-montserrat" href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
    </Head>
    <HomepagePopup :popup="popup" :baseurl="baseurl"></HomepagePopup>

    <section class="hero">
      <!-- Phones get the design's portrait crop, centred on her; wider
           screens the landscape photo with her on the right. -->
      <picture class="hero__picture">
        <source media="(max-width: 639px)" srcset="/img/home/hero-lady-mobile.webp" />
        <img
          class="hero__photo"
          src="/img/home/hero-lady.webp"
          srcset="/img/home/hero-lady-900.webp 900w, /img/home/hero-lady.webp 1774w"
          sizes="100vw"
          alt="Woman smiling at her laptop at home"
          fetchpriority="high"
        />
      </picture>
      <div class="hero__scrim" aria-hidden="true"></div>
      <div class="hero__content">
        <h1 class="hero__title">Your experience still counts.<br /><span>Let’s plan what happens next.</span></h1>
        <form class="search" role="search" @submit.prevent="searchJobs">
          <p class="search__intro">Search Returner Friendly jobs from employers who value your experience</p>
          <div class="search__fields">
            <input v-model="keyword" class="search__field" type="text" placeholder="Keyword/Title" aria-label="Keyword or job title" />
            <SearchSelect v-model="location" :options="locationOptions" placeholder="Location" label="Location" any-label="Any location" />
            <SearchSelect v-model="category" :options="categories" placeholder="Category" label="Category" any-label="Any category" />
          </div>
          <button type="submit" class="search__button">Search Jobs</button>
        </form>
      </div>
    </section>

    <section v-if="jobs.length" class="roles">
      <div class="roles__head">
        <h2 class="roles__title">Live Returner Friendly roles</h2>
        <Link href="/search/job" class="roles__all">See all {{ totalJobs }} jobs ⟶</Link>
      </div>
      <div class="roles__list">
        <article v-for="job in jobs" :key="job.id" class="job">
          <!-- A missing logo file hides the slot rather than showing a broken image. -->
          <img v-if="job.logo" class="job__logo" :src="'/img/' + job.logo" :alt="job.company" width="92" height="36" loading="lazy" @error="$event.target.style.display = 'none'" />
          <h3 class="job__title">{{ job.title }}</h3>
          <p class="job__meta">{{ job.company }} · Closing: {{ job.closing }}<template v-if="job.region"> · Region: {{ job.region }}</template></p>
          <div class="job__tags">
            <span v-for="tag in job.tags" :key="tag" class="job__tag">{{ tag }}</span>
          </div>
          <div class="job__actions">
            <button type="button" class="job__apply" @click="apply(job)">Apply</button>
            <Link :href="'/job/' + job.alias" class="job__details">Details</Link>
          </div>
        </article>
      </div>
    </section>

    <section class="panels">
      <div class="panel panel--roadmap">
        <img
          class="panel__photo"
          src="/img/home/roadmap-road.webp"
          srcset="/img/home/roadmap-road-800.webp 800w, /img/home/roadmap-road.webp 1400w"
          sizes="(min-width: 1024px) 50vw, 100vw"
          alt="Woman looking along a winding road at sunrise"
          loading="lazy"
        />
        <div class="panel__scrim" aria-hidden="true"></div>
        <div class="panel__body">
          <p class="panel__eyebrow">I’m returning to work</p>
          <h2 class="panel__title">Get your<br />Back to Work Roadmap</h2>
          <p class="panel__text">Get a personalised next-step plan, practical tools and support</p>
          <a href="/careerhub/back-to-work-roadmap/" class="cta">Start my FREE roadmap<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg></a>
        </div>
      </div>

      <div class="panel panel--employer">
        <div class="panel__body">
          <p class="panel__eyebrow">I’m an employer</p>
          <h2 class="panel__title">How Returner Friendly<br />is your recruitment?</h2>
          <p class="panel__text">Discover what you’re doing well and where you can improve</p>
          <a href="/careerhub/returner-friendly-recruitment-check/" class="cta">Request a FREE Check<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg></a>
        </div>
        <div class="checklist">
          <p class="checklist__label">RETURNER FRIENDLY CHECK</p>
          <ul class="checklist__items">
            <li v-for="item in checks" :key="item"><span aria-hidden="true">✓</span>{{ item }}</li>
          </ul>
          <p class="checklist__note">Five checks · one short form</p>
        </div>
      </div>
    </section>

    <section class="quick">
      <Link href="/search/course" class="quick__card">
        <svg width="58" height="58" viewBox="0 0 64 64" fill="none" stroke="#675B46" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M32 18c-6-4-13-5-20-4v30c7-1 14 0 20 4 6-4 13-5 20-4V14c-7-1-14 0-20 4z" /><path d="M32 18v30" /><path d="M17 23c4-.4 7-.2 10 1M17 30c4-.4 7-.2 10 1M37 24c3-1.2 6-1.4 10-1M37 31c3-1.2 6-1.4 10-1" /></svg>
        <span class="quick__text">
          <span class="quick__title">Find a course</span>
          <span class="quick__sub">Build New Skills for what's next</span>
        </span>
      </Link>
      <Link href="/events" class="quick__card">
        <svg width="54" height="54" viewBox="0 0 64 64" fill="none" stroke="#675B46" stroke-width="2.6" stroke-linecap="round" aria-hidden="true"><rect x="9" y="16" width="46" height="40" rx="4" /><path d="M19 10v10M32 10v10M45 10v10" /><g fill="#675B46" stroke="none"><rect x="17" y="30" width="6" height="6" rx="1.2" /><rect x="29" y="30" width="6" height="6" rx="1.2" /><rect x="41" y="30" width="6" height="6" rx="1.2" /><rect x="17" y="42" width="6" height="6" rx="1.2" /><rect x="29" y="42" width="6" height="6" rx="1.2" /><rect x="41" y="42" width="6" height="6" rx="1.2" /></g></svg>
        <span class="quick__text">
          <span class="quick__title">Join an Event</span>
          <span class="quick__sub">Meet, learn &amp; be inspired</span>
        </span>
      </Link>
      <a href="/careerhub/" class="quick__card">
        <svg width="54" height="54" viewBox="0 0 64 64" fill="none" stroke="#675B46" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M32 8v6M14 16l4 4M50 16l-4 4M8 34h6M50 34h6" /><path d="M32 20a13 13 0 0 0-7 24v4h14v-4a13 13 0 0 0-7-24z" /><path d="M27 52h10M28 56h8" /></svg>
        <span class="quick__text">
          <span class="quick__title">Explore our Career Hub</span>
          <span class="quick__sub">Practical tips &amp; resources</span>
        </span>
      </a>
    </section>

    <section v-if="marqueeItems.length" class="employers">
      <h2 class="employers__title">Meet our Returner-Friendly Employers</h2>
      <div class="marquee">
        <div class="marquee__track" :style="{ animationDuration: marqueeSeconds + 's' }">
          <!-- The second copy (and any repeats) are only there to make the
               loop seamless, so they are hidden from screen readers and tabbing. -->
          <div v-for="copy in 2" :key="copy" class="marquee__set" :aria-hidden="copy === 2 ? 'true' : null">
            <component
              :is="e.alias ? 'a' : 'span'"
              v-for="e in marqueeItems"
              :key="e.key"
              :href="e.alias ? '/organization/' + e.alias : null"
              :tabindex="e.alias && (copy === 2 || e.repeat) ? -1 : null"
              :aria-hidden="copy === 1 && e.repeat ? 'true' : null"
              class="marquee__item"
            >
              <img :src="'/img/' + e.logo" :alt="copy === 1 && !e.repeat ? e.name : ''" @error="$event.target.parentNode.style.display = 'none'" />
            </component>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue'
import Layout from '@/Shared/HomeLayout'
import HomepagePopup from '@/Shared/HomepagePopup'
import SearchSelect from '@/Shared/SearchSelect'

export default {
  props: {
    locations: Array,
    categories: Array,
    jobs: Array,
    totalJobs: Number,
    employers: Array,
    baseurl: String,
    auth: Object,
    popup: Object,
  },
  components: {
    Head,
    Link,
    HomepagePopup,
    SearchSelect,
  },
  layout: Layout,
  inject: {
    openLogin: { default: () => () => {} },
  },
  data() {
    return {
      keyword: '',
      location: '',
      category: '',
      checks: ['Visible', 'Accessible', 'Inclusive', 'Fair', 'Connected'],
    }
  },
  computed: {
    // Employers from the admin, repeated until one pass is long enough to
    // fill a wide screen, so a short list still loops without a gap.
    marqueeItems() {
      const base = (this.employers || []).filter(e => e.logo)
      if (!base.length) return []
      const items = []
      while (items.length < Math.max(base.length, 16)) items.push(...base)
      return items.map((e, i) => ({ ...e, key: i, repeat: i >= base.length }))
    },
    // The design's pace (11 logos in 40s) whatever the number of logos.
    marqueeSeconds() {
      return Math.round(this.marqueeItems.length * 3.6)
    },
    // "Remote" first, as on the old home page; the job search treats it as
    // remote work rather than a region.
    locationOptions() {
      return ['Remote', ...(this.locations || [])]
    },
  },
  methods: {
    searchJobs() {
      const params = {}
      if (this.keyword) params.keyword = this.keyword
      if (this.location) params.location = this.location
      if (this.category) params.category = this.category
      this.$inertia.get(this.baseurl + '/search/job', params)
    },
    // Same rules as Apply on the job page: sign in first; applicants go
    // straight to the employer's form; anything else opens the job page.
    apply(job) {
      const user = this.auth && this.auth.user
      if (!user) {
        this.openLogin('jobapply-' + job.id)
        return
      }
      const canApply = user.roles && (user.roles.includes('user') || user.roles.includes('admin'))
      if (canApply && /^https?:\/\//i.test(job.job_link || '')) {
        window.open(this.baseurl + '/job/' + job.alias + '/apply')
      } else {
        this.$inertia.visit(this.baseurl + '/job/' + job.alias)
      }
    },
  },
}
</script>

<style scoped>
.home {
  font-family: 'Montserrat', sans-serif;
  line-height: normal;
  color: #675B46;
  -webkit-font-smoothing: antialiased;
}
/* Zero-specificity resets so the section rules below always win. */
.home :where(p, h1, h2, h3, ul) {
  margin: 0;
  padding: 0;
}
.home :where(a) {
  text-decoration: none;
}
.home a:focus-visible,
.home button:focus-visible,
.home input:focus-visible,
.home select:focus-visible {
  outline: 3px solid #C99BC7;
  outline-offset: 2px;
}

/* ---------- Hero ---------- */
.hero {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 560px;
  margin-top: 18px;
  background: #F3EEE7;
}
/* The <picture> wrapper only chooses the file; the <img> is what lays out. */
.hero__picture {
  display: contents;
}
.hero__photo {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 100% 30%;
}
.hero__scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(246, 242, 236, .96) 0%, rgba(246, 242, 236, .9) 54%, rgba(246, 242, 236, .4) 66%, rgba(246, 242, 236, 0) 76%);
}
.hero__content {
  position: relative;
  width: 100%;
  padding: 54px clamp(20px, 4vw, 64px) 64px;
}
.hero__title {
  max-width: min(600px, 52%);
  font-size: clamp(30px, 4.2vw, 52px);
  line-height: 1.12;
  font-weight: 700;
  letter-spacing: -.01em;
  color: #560354;
  text-wrap: balance;
}
.hero__title span {
  color: #675B46;
}
.search {
  display: flex;
  flex-direction: column;
  gap: 22px;
  max-width: min(680px, 56%);
  min-width: 300px;
  margin-top: 34px;
  padding: 32px 34px 34px;
  border-radius: 28px;
  background: #560354;
}
.search__intro {
  font-size: 18px;
  font-weight: 600;
  line-height: 1.45;
  color: #FFFFFF;
}
.search__fields {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
}
.search__field {
  display: block;
  width: 100%;
  min-width: 0;
  padding: 16px 18px;
  border: 0;
  border-radius: 10px;
  background: #FFFFFF;
  font: 15px/1.2 'Montserrat', sans-serif;
  color: #3F3A32;
}
.search__field::placeholder {
  color: #9A9287;
  opacity: 1;
}
.search__button {
  align-self: center;
  padding: 16px 64px;
  border: 0;
  border-radius: 999px;
  background: #FFFFFF;
  font: 700 20px 'Montserrat', sans-serif;
  color: #560354;
  cursor: pointer;
  transition: background-color .15s;
}
.search__button:hover {
  background: #F6EFF5;
}

/* ---------- Live roles ---------- */
.roles {
  padding: 54px clamp(20px, 4vw, 64px) 0;
}
.roles__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 22px;
}
.roles__title {
  font-size: 24px;
  font-weight: 600;
  color: #560354;
}
/* Bigger and bolder than the design's 13px: it was easy to miss. */
.home .roles__all {
  font-size: 16px;
  font-weight: 700;
  color: #560354;
}
.home .roles__all:hover {
  color: #42003F;
  text-decoration: underline;
  text-underline-offset: 4px;
}
.roles__list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(235px, 1fr));
  gap: 24px;
}
.job {
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
  padding: 26px 24px 24px;
  border: 1px solid #EFEAE4;
  border-radius: 14px;
  background: #FFFFFF;
  box-shadow: 0 1px 3px rgba(0, 0, 0, .04);
}
.job__logo {
  display: block;
  align-self: flex-start;
  width: 92px;
  height: 36px;
  object-fit: contain;
  object-position: left center;
}
.job__title {
  font-size: 20px;
  font-weight: 600;
  color: #560354;
  overflow-wrap: anywhere;
}
.job__meta {
  font-size: 13px;
  color: #8A8074;
}
.job__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.job__tag {
  padding: 6px 10px;
  border: 1px solid #E6E0D9;
  border-radius: 8px;
  font-size: 11px;
  color: #8A8074;
}
.job__actions {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-top: auto;
  padding-top: 6px;
}
.job__apply {
  padding: 10px 26px;
  border: 0;
  border-radius: 999px;
  background: #560354;
  font: 600 14px 'Montserrat', sans-serif;
  color: #FFFFFF;
  cursor: pointer;
  transition: background-color .15s;
}
.job__apply:hover {
  background: #42003F;
}
.home .job__details {
  font-size: 14px;
  color: #675B46;
  text-decoration: underline;
}

/* ---------- Audience panels ---------- */
.panels {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 26px;
  padding: 44px clamp(20px, 4vw, 64px) 0;
}
.panel {
  border-radius: 18px;
}
.panel__body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.panel__eyebrow {
  font-size: 17px;
  font-weight: 500;
}
.panel__title {
  font-size: clamp(22px, 2.4vw, 30px);
  line-height: 1.18;
  font-weight: 700;
}
.panel__text {
  font-size: 16px;
  line-height: 1.5;
}
.panel--roadmap {
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: flex-end;
  min-height: 320px;
  overflow: hidden;
  background: #F6E9E1;
}
.panel__photo {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 0% 55%;
}
.panel__scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(270deg, rgba(252, 244, 238, .96) 0%, rgba(252, 244, 238, .9) 58%, rgba(252, 244, 238, .3) 76%, rgba(252, 244, 238, 0) 88%);
}
.panel--roadmap .panel__body {
  position: relative;
  width: min(400px, max(62%, 330px));
  padding: 26px 28px 28px;
}
.panel--roadmap .panel__eyebrow,
.panel--roadmap .panel__text {
  color: #675B46;
}
.panel--roadmap .panel__title {
  color: #560354;
}
.panel--employer {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(215px, 1fr));
  align-items: start;
  gap: 22px;
  min-height: 300px;
  padding: 26px 28px 28px;
  background: #A2968C;
}
.panel--employer .panel__body {
  height: 100%;
}
.panel--employer .panel__eyebrow {
  color: #F3EFEA;
}
.panel--employer .panel__title,
.panel--employer .panel__text {
  color: #FFFFFF;
}
.home .cta {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 10px;
  margin-top: auto;
  padding: 13px clamp(18px, 2vw, 26px);
  border-radius: 999px;
  background: #560354;
  box-shadow: 0 4px 14px rgba(86, 3, 84, .22);
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  color: #FFFFFF;
  white-space: nowrap;
  transition: background-color .15s;
}
.home .cta:hover {
  background: #42003F;
}
.checklist {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 16px 18px;
  border-radius: 10px;
  background: #FFFFFF;
}
.checklist__label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: .14em;
  line-height: 1.5;
  color: #8A8074;
}
.checklist__items {
  display: flex;
  flex-direction: column;
  gap: 10px;
  list-style: none;
}
.checklist__items li {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 13px;
  color: #4A4239;
}
.checklist__items span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #560354;
  font-size: 9px;
  color: #FFFFFF;
}
.checklist__note {
  margin-top: 6px;
  font-size: 11px;
  line-height: 1.45;
  color: #8A8074;
}

/* ---------- Quick links ---------- */
.quick {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 26px;
  padding: 34px clamp(20px, 4vw, 64px) 0;
}
.home .quick__card {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 22px 26px;
  border-radius: 20px;
  background: #FFFFFF;
  box-shadow: 0 6px 22px rgba(86, 3, 84, .08);
  color: #675B46;
  transition: box-shadow .2s;
}
.home .quick__card:hover {
  box-shadow: 0 10px 28px rgba(86, 3, 84, .14);
}
.quick__card svg {
  flex: 0 0 auto;
}
.quick__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.quick__title {
  font-size: 19px;
  font-weight: 700;
  color: #3F3A32;
}
.quick__sub {
  font-size: 17px;
  color: #675B46;
}

/* ---------- Employer logos ---------- */
.employers {
  padding: 54px 0 64px;
  text-align: center;
}
.employers__title {
  margin-bottom: 30px;
  font-size: 24px;
  font-weight: 700;
  color: #560354;
}
.marquee {
  position: relative;
  overflow: hidden;
  -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 8%, #000 92%, transparent 100%);
  mask-image: linear-gradient(90deg, transparent 0, #000 8%, #000 92%, transparent 100%);
}
.marquee__track {
  display: flex;
  width: max-content;
  animation: logoScroll 40s linear infinite;
}
.marquee__track:hover {
  animation-play-state: paused;
}
.marquee__set {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 56px;
  padding-right: 56px;
}
.marquee__item {
  display: block;
  flex: 0 0 auto;
}
/* Uploaded logos vary in shape, so each fits one box: wide marks fill the
   width, tall or square ones the height. */
.marquee__item img {
  display: block;
  width: auto;
  height: auto;
  max-width: 124px;
  max-height: 56px;
  object-fit: contain;
}
@keyframes logoScroll {
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
}
@media (prefers-reduced-motion: reduce) {
  .marquee {
    overflow-x: auto;
  }
  .marquee__track {
    animation: none;
  }
}

/* ---------- Tablet ---------- */
@media (max-width: 1023px) {
  .hero {
    flex-direction: column;
    align-items: stretch;
  }
  .hero__photo {
    position: static;
    order: 2;
    height: 380px;
    object-position: 80% 30%;
  }
  .hero__scrim {
    display: none;
  }
  .hero__title {
    max-width: 620px;
  }
  .search {
    max-width: none;
    min-width: 0;
  }
  .panels {
    grid-template-columns: minmax(0, 1fr);
    gap: 24px;
  }
  .panel__photo {
    object-position: 10% 60%;
  }
  .panel__scrim {
    background: linear-gradient(270deg, rgba(252, 244, 238, .96) 0%, rgba(252, 244, 238, .9) 48%, rgba(252, 244, 238, .3) 62%, rgba(252, 244, 238, 0) 74%);
  }
  .panel--roadmap .panel__body {
    width: 52%;
  }
}
@media (min-width: 640px) and (max-width: 1023px) {
  .roles__list {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
  }
  .job {
    padding: 20px 18px;
  }
  .quick {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
  }
  .home .quick__card {
    gap: 12px;
    padding: 18px 16px;
    border-radius: 18px;
  }
  .quick__card svg {
    width: 40px;
    height: 40px;
  }
  .quick__title {
    font-size: 16px;
  }
  .quick__sub {
    font-size: 13px;
    line-height: 1.35;
  }
}

/* ---------- Phone ---------- */
@media (max-width: 639px) {
  /* Photo first with the headline over a plum fade, then a white search
     card overlapping its foot (design "Home Page - Mobile 1c"). The markup
     is shared with desktop, so the hero becomes a grid: photo, fade and
     headline share row 1, the card sits in row 2 pulled up over it. */
  .hero {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    min-height: 0;
    margin-top: 0;
    padding-bottom: 28px;
    background: #FFFFFF;
  }
  .hero__content {
    display: contents;
  }
  .hero__photo,
  .hero__scrim,
  .hero__title {
    grid-area: 1 / 1;
  }
  .hero__photo {
    position: static;
    order: 0;
    height: 420px;
    object-position: 50% 10%;
  }
  .hero__scrim {
    display: block;
    position: static;
    background: linear-gradient(180deg, rgba(40, 20, 38, 0) 35%, rgba(56, 6, 54, .82) 80%, rgba(56, 6, 54, .92) 100%);
  }
  .hero__title {
    position: relative;
    align-self: end;
    max-width: none;
    padding: 0 20px 92px;
    font-size: 29px;
    line-height: 1.14;
    color: #FFFFFF;
    text-wrap: wrap;
  }
  .hero__title span {
    color: #F0DDEE;
  }
  .search {
    position: relative;
    z-index: 1;
    grid-area: 2 / 1;
    gap: 14px;
    max-width: none;
    min-width: 0;
    margin: -68px 16px 0;
    padding: 22px 18px;
    border-radius: 22px;
    background: #FFFFFF;
    box-shadow: 0 18px 40px rgba(40, 20, 38, .18);
  }
  .search__intro {
    font-size: 15px;
    color: #560354;
  }
  /* Keyword across the top; Location and Category side by side. */
  .search__fields {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }
  .search__fields > .search__field {
    grid-column: 1 / -1;
  }
  /* 16px (not the design's 15px) stops iOS Safari zooming the page when a
     field is focused. */
  .search__field,
  .search ::v-deep .ss__input {
    padding: 15px 16px;
    background: #F6F1F5;
    font-size: 16px;
  }
  .search ::v-deep .ss__input {
    padding: 15px 30px 15px 14px;
  }
  .search__field::placeholder,
  .search ::v-deep .ss__input::placeholder {
    color: #8A8074;
  }
  .search ::v-deep .ss__chevron {
    right: 14px;
    font-size: 10px;
  }
  .search__button {
    align-self: stretch;
    padding: 15px 0;
    background: #560354;
    font-size: 18px;
    color: #FFFFFF;
  }
  .search__button:hover {
    background: #42003F;
  }
  .roles__head {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 20px;
  }
  .home .roles__all {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    margin: -12px 0;
  }
  /* Swipe through the roles rather than stacking them. */
  .roles__list {
    grid-template-columns: none;
    grid-auto-flow: column;
    grid-auto-columns: 82%;
    gap: 14px;
    margin: 0 -20px;
    padding: 4px 20px 12px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scroll-padding: 0 20px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }
  .roles__list::-webkit-scrollbar {
    display: none;
  }
  .job {
    scroll-snap-align: start;
  }
  .job__apply {
    min-height: 44px;
  }
  .home .job__details {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
  }
  .panels {
    gap: 20px;
    padding-top: 44px;
  }
  .panel--roadmap {
    flex-direction: column;
    min-height: 0;
  }
  .panel__photo {
    position: static;
    height: 190px;
    object-position: 20% 60%;
  }
  .panel__scrim {
    display: none;
  }
  .panel--roadmap .panel__body {
    width: 100%;
  }
  .panel__title {
    font-size: 22px;
  }
  .home .cta {
    min-height: 44px;
    padding: 13px 18px;
  }
}
</style>
